import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableHead,
  TableRow, Button, Alert, IconButton, Chip, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Avatar,
} from '@mui/material';
import {
  Add, Home, Settings, Assessment, Refresh, Assignment, Search,
} from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import AppraisalCard from './AppraisalCard';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_COLOR = {
  Draft: 'default', ObjectivesSubmitted: 'info', ObjectivesApproved: 'info', InProgress: 'primary',
  SelfAppraised: 'warning', SupervisorReviewed: 'secondary', CommitteeReviewed: 'secondary',
  Completed: 'success', PIP: 'error',
};

const AppraisalList = () => {
  const navigate = useNavigate();
  const [appraisals, setAppraisals] = useState([]);
  const [periods, setPeriods]   = useState([]);
  const [employees, setEmployees] = useState([]);
  const [period, setPeriod]     = useState('');
  const [status, setStatus]     = useState('');
  const [search, setSearch]     = useState('');
  const [error, setError]       = useState('');
  const [openId, setOpenId]     = useState(null);
  const [newDlg, setNewDlg]     = useState(null);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: Boolean(newDlg), value: newDlg, onClose: () => setNewDlg(null) });
  const [newErr, setNewErr]     = useState('');

  const load = useCallback(async () => {
    try {
      const q = new URLSearchParams();
      if (period) q.set('periodCode', period);
      if (status) q.set('status', status);
      const [aRes, pRes] = await Promise.all([
        axios.get(`${API}/api/appraisal/appraisals?${q}`, { headers: hdrs() }),
        axios.get(`${API}/api/appraisal/periods`, { headers: hdrs() }),
      ]);
      setAppraisals(aRes.data); setPeriods(pRes.data); setError('');
    } catch (e) { setError(e.response?.data?.error || 'Failed to load appraisals'); }
  }, [period, status]);
  useEffect(() => { load(); }, [load]);

  const openNew = async () => {
    try { const r = await axios.get(`${API}/api/employees`, { headers: hdrs() }); setEmployees(r.data); }
    catch { setEmployees([]); }
    setNewErr('');
    setNewDlg({ periodCode: period || (periods[0]?.code || ''), employeeNo: '' });
  };

  const createAppraisal = async () => {
    setNewErr('');
    try {
      const r = await axios.post(`${API}/api/appraisal/appraisals`, newDlg, { headers: hdrs() });
      setNewDlg(null); await load(); setOpenId(r.data._id);
    } catch (e) { setNewErr(e.response?.data?.error || 'Could not create appraisal'); }
  };

  const filtered = appraisals.filter(a => {
    const q = search.trim().toLowerCase();
    return !q || (a.employeeName || '').toLowerCase().includes(q) || (a.employeeNo || '').toLowerCase().includes(q);
  });

  return (
    <Box sx={{ maxWidth: 1150, mx: 'auto', mt: 4, mb: 4, px: 2 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Assignment color="primary" />
              <Typography variant="h5" color="primary">Performance Appraisals</Typography>
              <Chip label={filtered.length} size="small" color="primary" />
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Reports"><IconButton onClick={() => navigate('/hr/appraisal/reports')}><Assessment /></IconButton></Tooltip>
              <Tooltip title="Setup"><IconButton onClick={() => navigate('/hr/appraisal/setup')}><Settings /></IconButton></Tooltip>
              <Tooltip title="Refresh"><IconButton onClick={load}><Refresh /></IconButton></Tooltip>
              <Tooltip title="HR Home"><IconButton onClick={() => navigate('/hr')}><Home /></IconButton></Tooltip>
              <Button variant="contained" startIcon={<Add />} onClick={openNew}>New Appraisal</Button>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* Filters */}
          <Box display="flex" gap={1.5} mb={2} flexWrap="wrap">
            <TextField select size="small" label="Period" value={period} onChange={e => setPeriod(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value="">All periods</MenuItem>
              {periods.map(p => <MenuItem key={p.code} value={p.code}>{p.code} — {p.name}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Status" value={status} onChange={e => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
              <MenuItem value="">All statuses</MenuItem>
              {['Draft', 'ObjectivesSubmitted', 'ObjectivesApproved', 'InProgress', 'SelfAppraised', 'SupervisorReviewed', 'CommitteeReviewed', 'Completed', 'PIP'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField size="small" placeholder="Search employee…" value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }} sx={{ minWidth: 200 }} />
          </Box>

          <Table size="small">
            <TableHead><TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell><strong>Employee</strong></TableCell>
              <TableCell><strong>Period</strong></TableCell>
              <TableCell><strong>Supervisor</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="center"><strong>Self</strong></TableCell>
              <TableCell align="center"><strong>Final</strong></TableCell>
              <TableCell><strong>Rating</strong></TableCell>
            </TableRow></TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center"><Typography variant="body2" color="text.secondary" py={2}>No appraisals. Click “New Appraisal” to start one.</Typography></TableCell></TableRow>
              ) : filtered.map(a => (
                <TableRow key={a._id} hover sx={{ cursor: 'pointer' }} onClick={() => setOpenId(a._id)}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Avatar sx={{ width: 30, height: 30, bgcolor: '#546e7a22', color: '#546e7a', fontSize: 13, fontWeight: 700 }}>{(a.employeeName || '?').charAt(0)}</Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={600} lineHeight={1.2}>{a.employeeName}</Typography>
                        <Typography variant="caption" color="text.secondary">{a.employeeNo} · {a.department || '—'}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{a.periodCode}</TableCell>
                  <TableCell><Typography variant="caption">{a.supervisorName || a.supervisorNo || '—'}</Typography></TableCell>
                  <TableCell><Chip label={a.status} size="small" color={STATUS_COLOR[a.status] || 'default'} /></TableCell>
                  <TableCell align="center">{a.selfOverall != null ? `${a.selfOverall}%` : '—'}</TableCell>
                  <TableCell align="center">{a.finalScore != null ? `${a.finalScore}%` : '—'}</TableCell>
                  <TableCell><Typography variant="caption">{a.finalRating || '—'}</Typography></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* New appraisal dialog */}
      {discardDialog}
      <Dialog open={Boolean(newDlg)} onClose={attemptClose} maxWidth="xs" fullWidth>
        <DialogTitle>New Appraisal</DialogTitle>
        <DialogContent dividers>
          {newErr && <Alert severity="error" sx={{ mb: 2 }}>{newErr}</Alert>}
          {newDlg && (
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              <TextField select label="Period *" value={newDlg.periodCode} onChange={e => setNewDlg({ ...newDlg, periodCode: e.target.value })} fullWidth size="small">
                {periods.map(p => <MenuItem key={p.code} value={p.code}>{p.code} — {p.name}</MenuItem>)}
              </TextField>
              <TextField select label="Employee *" value={newDlg.employeeNo} onChange={e => setNewDlg({ ...newDlg, employeeNo: e.target.value })} fullWidth size="small">
                {employees.map(e => <MenuItem key={e.employeeNo} value={e.employeeNo}>{e.employeeNo} — {e.firstName} {e.lastName}</MenuItem>)}
              </TextField>
              <Typography variant="caption" color="text.secondary">
                The employee can then set objectives (with KPI, weight, target & milestones) and submit them for supervisor approval.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" disabled={!newDlg?.periodCode || !newDlg?.employeeNo} onClick={createAppraisal}>Create</Button>
        </DialogActions>
      </Dialog>

      {openId && <AppraisalCard appraisalId={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </Box>
  );
};

export default AppraisalList;
